import React, { useState, useEffect } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";

import apiService from "../../services/ApiService";
import type { VerificatorMember } from "../../types/api";
import { getDeviceID } from "../../constants/auth";

interface OperatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, shiftId?: string) => void;
}

export const OperatorModal: React.FC<OperatorModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [selectedVerificator, setSelectedVerificator] = useState("");
  const [verificatorUsername, setVerificatorUsername] = useState("");
  const [verificatorMembers, setVerificatorMembers] = useState<
    VerificatorMember[]
  >([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Load verificator members when modal opens
  useEffect(() => {
    if (isOpen) {
      loadVerificatorMembers();
      setSelectedVerificator("");
      setError("");
    }
  }, [isOpen]);

  const loadVerificatorMembers = async () => {
    setIsLoading(true);
    try {
      // Try to sync from API first if available
      const response = await apiService.getMembers({
        params: { role: "verificator" },
      });

      if (response) {
        // Update local storage with API data
        const apiMembers = (response as unknown as VerificatorMember[]).map(
          (member) =>
            ({
              full_name: member.full_name,
              username: member.username,
              created_at: member.created_at,
            } as VerificatorMember)
        );

        localStorage.setItem(
          "lumibot:verificatorMembers",
          JSON.stringify(apiMembers)
        );
        setVerificatorMembers(apiMembers);
      }
    } catch (error) {
      setError(JSON.stringify(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedVerificator.trim()) {
      setError("");
    } else {
      setError("Please select a verificator");
      return;
    }
    setIsLoading(true);
    setError("");

    try {
      // Create a new shift when operator is selected

      await apiService.authenticate({
        username: verificatorUsername,
        role: "verificator",
      });

      const newShift = await apiService.createShift({
        device_id: getDeviceID(),
        cell_type: "verification",
      });

      const shiftId = newShift.id;
      console.log("New shift created:", newShift);

      // Store shift ID in localStorage for this session
      localStorage.setItem("current_shift_id", shiftId);
      localStorage.setItem("current_verificator", selectedVerificator);

      // Continue with existing flow, passing shift ID
      onSubmit(selectedVerificator.trim(), shiftId);
      setSelectedVerificator("");
      setError("");
    } catch (error) {
      console.error("Failed to create shift:", error);
      setError("Failed to create shift. Please try again.");
    } finally {
      setIsLoading(false);
      // onSubmit(selectedVerificator.trim());
      setSelectedVerificator(""); // Clear selection after submit
    }
  };

  const handleClose = () => {
    setSelectedVerificator(""); // Clear selection when closing
    setError("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Select Verificator"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || !selectedVerificator.trim()}
          >
            {isLoading ? "Creating Shift..." : "Continue"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading verificators...</span>
          </div>
        ) : verificatorMembers.length > 0 ? (
          <>
            <label className="block text-sm font-medium text-gray-700">
              Choose your name from the list:
            </label>
            <select
              value={selectedVerificator}
              onChange={(e) => {
                setSelectedVerificator(e.target.value);

                console.log(verificatorMembers);

                const member = verificatorMembers.filter(
                  (member) => member.full_name === e.target.value
                );

                if (member.length > 0) {
                  setVerificatorUsername(member[0].username);
                } else {
                  setVerificatorUsername("");
                }

                setError("");
              }}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            >
              <option value="">-- Select Verificator --</option>
              {verificatorMembers.map((member) => (
                <option key={member.id} value={member.full_name}>
                  {member.full_name}
                </option>
              ))}
            </select>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-600 mb-2">
              No verificators have been set up yet.
            </p>
            <p className="text-sm text-gray-500">
              Please ask your manager to add verificator members in the Manager
              Settings.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
